/**
 * apps/web :: lib/auth-api
 *
 * Typed wrappers for the custom-UI WorkOS auth endpoints. Each function is a
 * thin `apiFetch` call — no state, no side-effects. Callers own navigation and
 * context refresh after a successful sign-in.
 */
import { apiFetch } from "@/lib/api";

export interface SignUpResult {
  /** "ok" when the user was already verified and a session was created. */
  status: "ok" | "verification_sent";
  email?: string;
}

/**
 * Create a new account via `POST /auth/signup`.
 *
 * Returns `{ status: "verification_sent", email }` when WorkOS sends a
 * verification email (the normal path). Returns `{ status: "ok" }` in the
 * rare case the user was already verified (admin-created accounts).
 * Throws {@link ApiError} on validation or duplicate-email errors.
 */
export async function signUp(
  email: string,
  password: string,
  firstName?: string,
  lastName?: string,
): Promise<SignUpResult> {
  return apiFetch<SignUpResult>("/auth/signup", {
    method: "POST",
    body: JSON.stringify({
      email,
      password,
      ...(firstName ? { first_name: firstName } : {}),
      ...(lastName ? { last_name: lastName } : {}),
    }),
  });
}

/**
 * Sign in via `POST /auth/signin`. Sets the session cookie on success.
 * Throws {@link ApiError} with `status=401` on invalid credentials or
 * `status=403` when the user's email is not yet verified.
 */
export async function signIn(email: string, password: string): Promise<void> {
  await apiFetch("/auth/signin", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

/**
 * Send a password-reset email via `POST /auth/forgot-password`.
 * Always resolves — the backend swallows WorkOS errors to prevent email
 * enumeration. Callers should always show "if the address is registered,
 * you'll get an email" copy.
 */
export async function forgotPassword(email: string): Promise<void> {
  await apiFetch("/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/**
 * Confirm a password reset via `POST /auth/reset-password`.
 * Throws {@link ApiError} with `status=400` when the token is expired or
 * already used, or if the new password violates WorkOS policy.
 */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await apiFetch("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}
